import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Context as RestaurantContext } from '../context/restaurant';
import { Context as CheckoutContext } from '../context/checkout';
import { Context as AlertContext } from '../context/alert';
import { Context as AuthContext } from '../context/auth';
import { Context as CartContext } from '../context/cart';
import MainLayout from '../layouts/mainLayout';
import CheckoutBill from '../features/checkout/components/bill';
import TopBar from '../layouts/place/topBar';
import { initiatePayment } from '../api/payment';
import Loader from '../components/misc/loader';
import { formatCartForBill } from '../services/cartService';
import { restaurantTypes } from '../enums/restaurantTypes';
import { useTableSseConnection } from '../hooks/useTableSseConnection';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useTableSseConnection();

  const { state: restaurantState, setNeedRefreshOrders } = useContext(RestaurantContext);
  const { alertController } = useContext(AlertContext);
  const { state: checkoutState, setSplitBill, changeBillDiscount } = useContext(CheckoutContext);
  const { registerGuest } = useContext(AuthContext);
  const { clearCart } = useContext(CartContext);

  const [initiating, setInitiating] = useState(false);
  const [paymentKey, setPaymentKey] = useState(null);

  useEffect(() => {
    if (!checkoutState.bill) {
      if (restaurantState.restaurant.type === restaurantTypes.OrderAndPay) {
        navigate('/cart', { replace: true });
      } else {
        navigate('/bill', { replace: true });
      }
    }
  }, []);

  const startPayment = async ({
    paymentType,
    tips,
    paymentMethodId,
    employeeId,
    dontDisable,
    paymentTrackId,
    paymentAccountId,
  }) => {
    if (!dontDisable) {
      setInitiating(true);
    }

    const { token } = await registerGuest();
    if (!token) {
      setInitiating(false);
      return;
    }

    const response = await initiatePayment({
      token,
      alertController,
      orderId: checkoutState.bill.orderId,
      paymentType,
      subtotal: checkoutState.bill.subtotal,
      discount: checkoutState.bill.discount,
      items: checkoutState.bill.items,
      restaurantType: restaurantState.restaurant.type,
      restaurantId: restaurantState.restaurant.id,
      tableId: restaurantState.table.id,
      totalGuests: checkoutState.bill.totalGuests,
      roomNumber: checkoutState.bill.roomNumber,
      tips,
      paymentMethodId,
      paymentAccountId,
      cart: formatCartForBill({ cart: checkoutState.bill.cart }),
      note: checkoutState.bill.note,
      coupon: null,
      employeeId,
    });

    if (response && response.isCashPayment) {
      if (response.orderId) {
        clearCart();
        setNeedRefreshOrders();
        navigate(`/order/${response.orderId}`);
        return;
      } else {
        setInitiating(false);
        return;
      }
    }

    if (response && response.status === 'discountChange') {
      changeBillDiscount(response.actualDiscount);
      setInitiating(false);
      return;
    }

    if (response && response.status === 'Created' && response.paymentLink) {
      // eslint-disable-next-line no-undef
      window.location.replace(response.paymentLink);
      return;
    }

    if (response && response.status === 'Created' && response.paymentKey) {
      if (paymentTrackId) {
        setPaymentKey(`${paymentTrackId}~${response.paymentKey || ''}`);
      } else {
        setPaymentKey(response.paymentKey);
      }

      return;
    }

    setInitiating(false);
  };

  if (!checkoutState.bill) {
    return <Loader />;
  }

  const order = restaurantState.orders
    ? restaurantState.orders.find((x) => x.id === checkoutState.bill.orderId)
    : null;

  return (
    <MainLayout showPayments>
      <div className="sticky top-0 bg-white z-10">
        <TopBar
          title={t('header.payment')}
          onBack={() =>
            navigate(
              restaurantState.restaurant.type === restaurantTypes.OrderAndPay ? '/cart' : '/bill',
            )
          }
        />
      </div>
      <CheckoutBill
        bill={checkoutState.bill}
        order={order}
        table={restaurantState.table}
        paymentOption={restaurantState.paymentOption}
        onPay={startPayment}
        disabled={initiating}
        onOpenBill={() => {
          navigate('/bill');
        }}
        onBillSplitChange={(splitType, data) => {
          setSplitBill({ splitType, data });
        }}
        onFail={(errorMessage) => {
          setPaymentKey(null);
          setInitiating(false);

          alertController.error({ text: errorMessage || t('errors.unknown') });
        }}
        paymentKey={paymentKey}
        allowTips={restaurantState.restaurant.allowTipsInOrder}
        restaurant={restaurantState.restaurant}
      />
    </MainLayout>
  );
};

export default CheckoutPage;
